import type { ReactNode } from 'react';
import { Spinner, StatusBadge, type StatusBadgeTone } from '@immo/ui-kit';
import type { LandIqBoardStageState, LandIqDataRunFailureReason } from '@immo/api-contract';
import { CheckCircleIcon, ClockIcon, HalfCircleIcon, XCircleIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';
import { isSourceOutage, SOURCE_OUTAGE_TONE } from '@/lib/landiq/entry-state-tones';

const ICON_CLASS = 'h-3.5 w-3.5';

const TAGS: Record<LandIqBoardStageState, { tone: StatusBadgeTone; icon: ReactNode }> = {
  RUNNING: { tone: 'info', icon: <Spinner size="sm" /> },
  DONE: { tone: 'success', icon: <CheckCircleIcon className={ICON_CLASS} /> },
  FAILED: { tone: 'danger', icon: <XCircleIcon className={ICON_CLASS} /> },
  INCOMPLETE: { tone: 'warning', icon: <HalfCircleIcon className={ICON_CLASS} /> },
};

type Props = {
  readonly state: LandIqBoardStageState;
  readonly failureReason?: LandIqDataRunFailureReason | null;
};

/** A stage's state as LandIQ returns it, by name and icon. A failure because the source was down reads calmer. */
export function ColumnStateTag({ state, failureReason }: Props) {
  const t = useTranslations('pages.admin.landiq-data-runs');

  if (isSourceOutage(state, failureReason)) {
    return (
      <StatusBadge
        size="sm"
        tone={SOURCE_OUTAGE_TONE}
        label={t('failure-reasons.SOURCE_OUTAGE')}
        icon={<ClockIcon className={ICON_CLASS} />}
      />
    );
  }

  const { tone, icon } = TAGS[state];

  return <StatusBadge size="sm" tone={tone} label={t(`board.column-states.${state}`)} icon={icon} />;
}
